import { useEffect, useState, useMemo } from "react";
import "./Transacciones.css";
import { buscarTransacciones, listarEstadosTransaccion } from "../../services/TransaccionService";
import { buscarProductoPorId } from "../../services/NomencladorService";
import { Spinner, SortIcon, Badge, Field } from "./TransactionComponents";

/* const estados = [
  { value: 1, label: "Autorizado", aliases: ["AUTORIZADO", "1"] },
  { value: 4, label: "Iniciado", hidden: true, aliases: ["INICIADO", "4"] },
  { value: 3, label: "Derivado", aliases: ["DERIVADO", "5"] },
  { value: 2, label: "No Autorizado", aliases: ["RECHAZADO", "NO_AUTORIZADO", "6"] },
  { value: 9, label: "Requiere más info", hidden: true },
]; */

const mapEstadoCatalogo = (estado) => ({
  value: estado.idEstadoPrestacion,
  nombre: estado.nombre,
  label: String(estado.nombre || "")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" "),
});

const opcionesFiltroFecha = [
  { value: "TODOS", label: "Todos" },
  { value: "HOY", label: "Hoy" },
  { value: "ULTIMOS_7_DIAS", label: "Últimos 7 días" },
  { value: "ULTIMOS_15_DIAS", label: "Últimos 15 días" },
  { value: "ULTIMO_MES", label: "Último mes" },
  { value: "MES_ACTUAL", label: "Mes actual" },
];

const buildEstadosMap = (items) => items.reduce((acc, curr) => {
  acc[curr.value] = curr.label;
  if (curr.nombre) acc[curr.nombre] = curr.label;
  curr.aliases?.forEach((alias) => {
    acc[alias] = curr.label;
  });
  return acc;
}, {});

const COLS = [
  { key: "timestampCreacion",   label: "Fecha Transacción" },
  { key: "fechaOrden",         label: "Fecha Orden" },
  { key: "idTransaccionTabla", label: "ID Transacción" },
  { key: "idAfiliado",         label: "Nro Afiliado" },
  { key: "descripcionItem",    label: "Descripción Item" },
  { key: "cantidadSolicitada", label: "Cant. Solicitada" },
  { key: "cantidadAutorizada", label: "Cant. Autorizada" },
  { key: "estado",             label: "Estado" },
  { key: "motivo",             label: "Motivo" },
];

const PAGE_SIZE = 10;

const getUltimoHistorial = (t) => {
  const historial = t.items?.[0]?.historialEstados;
  if (!Array.isArray(historial) || !historial.length) return null;

  return historial.reduce((ultimo, actual) => {
    const timestampUltimo = Date.parse(ultimo?.timestamp);
    const timestampActual = Date.parse(actual?.timestamp);
    if (Number.isNaN(timestampActual)) return ultimo;
    if (Number.isNaN(timestampUltimo) || timestampActual >= timestampUltimo) return actual;
    return ultimo;
  });
};

const fallbackEstadoLabel = (estado) => {
  if (!estado) return "-";
  return String(estado)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getEstado = (t, estadoLabels = {}) => {
  const ultimo = getUltimoHistorial(t);
  const estado = ultimo?.estado || t.items?.[0]?.estado || t.estadoGeneral;
  return estadoLabels[estado] || estadoLabels[Number(estado)] || fallbackEstadoLabel(estado);
};

const getMotivo = (t) => {
  const ultimo = getUltimoHistorial(t);
  return ultimo?.motivo || "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR");
};

const getFiltroFechaLabel = (value) =>
  opcionesFiltroFecha.find((opt) => opt.value === value)?.label || "Todos";

export default function Transacciones() {
  const [fechaInicio,   setFechaInicio]   = useState("");
  const [fechaHasta,    setFechaHasta]    = useState("");
  const [nroAfiliado,   setNroAfiliado]   = useState("");
  const [estado,        setEstado]        = useState("");
  const [prestacion,    setPrestacion]    = useState("");
  const [filtroFecha,   setFiltroFecha]   = useState("TODOS");
  const [modoFecha,     setModoFecha]     = useState("RANGO"); // 'RANGO' o 'CUSTOM'
  const [transacciones, setTransacciones] = useState([]);
  const [estadosCatalogo, setEstadosCatalogo] = useState([]);
  const [estadosEstadoCarga, setEstadosEstadoCarga] = useState("cargando");
  const [loading,       setLoading]       = useState(false);
  const [hasSearched,   setHasSearched]   = useState(false);
  const [sortConfig,    setSortConfig]    = useState({ key: "fechaOrden", dir: "asc" });
  const [page,          setPage]          = useState(0);
  const [filtersOpen,   setFiltersOpen]   = useState(false);
  const [expandedRows,  setExpandedRows]  = useState({});

  const handleNumeroChange = (e) => setNroAfiliado(e.target.value.replace(/[^0-9]/g, ""));
  const handleFechaInicioChange = (e) => {
    setModoFecha("CUSTOM");
    setFechaInicio(e.target.value);
  };
  const handleFechaHastaChange = (e) => {
    setModoFecha("CUSTOM");
    setFechaHasta(e.target.value);
  };

  useEffect(() => {
    let isMounted = true;
    listarEstadosTransaccion()
      .then((data) => {
        if (!isMounted) return;
        const items = (Array.isArray(data) ? data : [])
          .filter((item) => String(item.nombre || "").toLowerCase() !== "string")
          .map(mapEstadoCatalogo);
        setEstadosCatalogo(items);
        setEstadosEstadoCarga(items.length ? "ok" : "vacio");
      })
      .catch(() => {
        if (!isMounted) return;
        setEstadosCatalogo([]);
        setEstadosEstadoCarga("error");
      });
    return () => { isMounted = false; };
  }, []);

  const estadoLabels = useMemo(() => buildEstadosMap(estadosCatalogo), [estadosCatalogo]);

  const handleFetch = async () => {
    setLoading(true);
    setHasSearched(true);
    setPage(0);
    try {
      const filtros = {
        numero:      nroAfiliado ? parseInt(nroAfiliado) : null,
        estado:      estado      ? parseInt(estado)      : null,
        prestacion:  prestacion  ? prestacion            : null,
        filtroFecha: modoFecha === "RANGO" && filtroFecha !== "TODOS" ? filtroFecha : null,
        fechaDesde:  modoFecha === "CUSTOM" ? (fechaInicio || null) : null,
        fechaHasta:  modoFecha === "CUSTOM" ? (fechaHasta  || null) : null,
      };

       const data = await buscarTransacciones(filtros);
       const enriched = await Promise.all(
         data.map(async (t) => {
          const idProducto = t.items?.[0]?.idProducto;
         if (!idProducto) return { ...t, descripcionItem: "-" };
            try {
                const producto = await buscarProductoPorId(idProducto);
                return { ...t, descripcionItem: producto?.descripcion || "-" };
            } catch { return { ...t, descripcionItem: "-" }; }
            })
        );
       setTransacciones(enriched);
    } catch (error) {
      setTransacciones([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshParameters = () => {
    setFechaInicio(""); setFechaHasta(""); setNroAfiliado("");
    setEstado(""); setPrestacion(""); setFiltroFecha("TODOS"); setTransacciones([]); setHasSearched(false); setPage(0);
  };

  const toggleSort = (key) =>
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const toggleExpanded = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleHeaderSearch = (event) => {
    event.stopPropagation();
    handleFetch();
  };

  const sorted = useMemo(() => {
    if (!sortConfig.key) return transacciones;
    return [...transacciones].sort((a, b) => {
      let av = a[sortConfig.key];
      let bv = b[sortConfig.key];
      if (sortConfig.key === "fechaOrden") { av = new Date(av); bv = new Date(bv); }
      if (sortConfig.key === "estado")     { av = getEstado(a, estadoLabels); bv = getEstado(b, estadoLabels); }
      if (av < bv) return sortConfig.dir === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.dir === "asc" ?  1 : -1;
      return 0;
    });
  }, [transacciones, sortConfig, estadoLabels]);

  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRows  = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const filtrosActivos = [
    modoFecha === "RANGO"
      ? getFiltroFechaLabel(filtroFecha)
      : `${fechaInicio || "Desde"} - ${fechaHasta || "Hasta"}`,
    nroAfiliado ? `Afiliado/DNI ${nroAfiliado}` : null,
    estado ? estadoLabels[Number(estado)] : null,
    prestacion || null,
  ].filter(Boolean);

  return (
    <div className="tx-page">

      {/* Card */}
      <div className="tx-card">

        {/* Card header — clickable para colapsar */}
        <div className="tx-card-header tx-card-header--toggle" onClick={() => setFiltersOpen(o => !o)}>
          <div className="tx-card-header-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M2 8h8M2 12h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="tx-card-header-title">Filtros</h2>
          <div className="tx-card-header-summary">
            {filtrosActivos.map((filter) => (
              <span key={filter} className="tx-filter-pill">{filter}</span>
            ))}
          </div>
          {!filtersOpen && (
            <button
              type="button"
              className="tx-btn-search tx-btn-search--header"
              onClick={handleHeaderSearch}
              disabled={loading}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M9 17A8 8 0 1 1 9 1a8 8 0 0 1 0 16ZM15 15l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Buscar
            </button>
          )}
          <span className="tx-card-header-chevron">{filtersOpen ? '▲' : '▼'}</span>
        </div>

        {/* Filters */}
        {filtersOpen && <div className="tx-filters">
          <div className="tx-filters-grid">
            <div className="tx-date-panel">
              <div className="tx-field-group">
              <label className="tx-mode-selector">
                <input
                  type="radio"
                  name="modoFecha"
                  checked={modoFecha === "RANGO"}
                  onChange={() => setModoFecha("RANGO")}
                />
                <span className="tx-field-label">Rango Predefinido</span>
              </label>
              <select
                className="tx-select"
                value={filtroFecha}
                onChange={(e) => setFiltroFecha(e.target.value)}
                disabled={modoFecha !== "RANGO"}
              >
                {opcionesFiltroFecha.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="tx-field-group">
              <label className="tx-mode-selector">
                <input
                  type="radio"
                  name="modoFecha"
                  checked={modoFecha === "CUSTOM"}
                  onChange={() => setModoFecha("CUSTOM")}
                />
                <span className="tx-field-label">Rango Personalizado</span>
              </label>
              <div className="tx-date-inputs">
                <input
                  type="date"
                  className="tx-input"
                  value={fechaInicio}
                  onChange={handleFechaInicioChange}
                  placeholder="Desde"
                />
                <input
                  type="date"
                  className="tx-input"
                  value={fechaHasta}
                  onChange={handleFechaHastaChange}
                  placeholder="Hasta"
                />
              </div>
            </div>

            </div>

            <div className="tx-filter-fields">
              <Field label="Nro. Asociado o DNI">
              <input
                type="text"
                className="tx-input"
                value={nroAfiliado}
                onChange={handleNumeroChange}
                placeholder="0"
              />
            </Field>

            <Field label="Estado">
              <select
                className={`tx-select${!estado ? " placeholder" : ""}`}
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="" disabled hidden>
                  {estadosEstadoCarga === "cargando" && "Cargando estados..."}
                  {estadosEstadoCarga === "ok" && "Seleccione un estado"}
                  {estadosEstadoCarga === "vacio" && "No hay estados configurados"}
                  {estadosEstadoCarga === "error" && "No se pudieron cargar los estados"}
                </option>
                {estadosCatalogo
                  .filter((e) => !e.hidden)
                  .map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
              </select>
            </Field>

            <Field label="codigo o nombre de prestacion">
              <input
                type="text"
                className="tx-input"
                value={prestacion}
                onChange={(e) => setPrestacion(e.target.value)}
                placeholder="Código o nombre..."
              />
            </Field>
            </div>
          </div>

          {/* Actions */}
          <div className="tx-actions">
            <button className="tx-btn-clear" onClick={refreshParameters}>
              Limpiar
            </button>
            <button
              className="tx-btn-search"
              onClick={handleFetch}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="tx-btn-spinner" />
                  Buscando…
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="white" strokeWidth="1.5" />
                    <path d="M9.5 9.5L12.5 12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Buscar
                </>
              )}
            </button>
          </div>
        </div>}

        {/* Results */}
        <div className="tx-results-section">
          {!hasSearched && !loading ? (

            <div className="tx-empty-prompt">
              <div className="tx-empty-prompt-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="#B04708" strokeWidth="1.5" strokeLinecap="round" />
                  <rect x="9" y="3" width="6" height="4" rx="1.5" stroke="#B04708" strokeWidth="1.5" />
                  <path d="M9 12h6M9 16h4" stroke="#B04708" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <p className="tx-empty-prompt-text">
                Aplicá filtros y presioná <strong>Buscar</strong> para ver los resultados
              </p>
            </div>

          ) : (

            <div className="tx-results">

              {/* Meta bar */}
              {!loading && (
                <div className="tx-results-meta">
                  <span className="tx-results-count">
                    {transacciones.length === 0
                      ? "Sin resultados"
                      : `${transacciones.length} resultado${transacciones.length !== 1 ? "s" : ""}`}
                  </span>
                  {transacciones.length > 0 && (
                    <span className="tx-results-badge">● Resultados</span>
                  )}
                </div>
              )}

              {/* Table or spinner */}
              {loading ? (
                <Spinner />
              ) : (
                <>
                  <div className="tx-list-tools">
                    <span className="tx-list-tools-label">Ordenar por</span>
                    <div className="tx-sort-controls">
                      {COLS.map((col) => (
                        <button
                          key={col.key}
                          type="button"
                          className={`tx-sort-chip${sortConfig.key === col.key ? " active" : ""}`}
                          onClick={() => toggleSort(col.key)}
                        >
                          {col.label}
                          <SortIcon direction={sortConfig.key === col.key ? sortConfig.dir : null} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="tx-list" role="list">
                    {pageRows.length === 0 ? (
                      <div className="tx-table-empty">
                        <div className="tx-table-empty-inner">
                          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                            <circle cx="16" cy="16" r="11" stroke="#D1C4BC" strokeWidth="2" />
                            <path d="M24 24L31 31" stroke="#D1C4BC" strokeWidth="2" strokeLinecap="round" />
                            <path d="M12 16H20M16 12V20" stroke="#D1C4BC" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <span className="tx-table-empty-text">
                            No hay resultados para esta busqueda
                          </span>
                        </div>
                      </div>
                    ) : (
                      pageRows.map((row) => {
                        const rowId = row.idTransaccionTabla;
                        const expanded = Boolean(expandedRows[rowId]);

                        return (
                          <article key={rowId} className={`tx-result-row${expanded ? " expanded" : ""}`} role="listitem">
                            <button
                              type="button"
                              className="tx-result-main"
                              onClick={() => toggleExpanded(rowId)}
                              aria-expanded={expanded}
                            >
                              <span className="tx-row-chevron" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                  <path d="M5 7L9 11L13 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>

                              <span className="tx-row-id">#{rowId}</span>

                              <span className="tx-row-content">
                                <span className="tx-row-title">{row.descripcionItem || "-"}</span>
                                <span className="tx-row-subtitle">
                                  Orden {formatDate(row.fechaOrden)} - Afiliado {row.idAfiliado || "-"}
                                </span>
                              </span>

                              <span className="tx-row-metrics">
                                <span className="tx-row-metric">
                                  <span className="tx-row-metric-label">Solic.</span>
                                  <span className="tx-row-metric-value">{row.cantidadSolicitada || 0}</span>
                                </span>
                                <span className="tx-row-metric">
                                  <span className="tx-row-metric-label">Aut.</span>
                                  <span className="tx-row-metric-value">{row.cantidadAutorizada || 0}</span>
                                </span>
                              </span>

                              <span className="tx-row-status">
                                <Badge label={getEstado(row, estadoLabels)} />
                              </span>
                            </button>

                            {expanded && (
                              <div className="tx-result-details">
                                <div className="tx-detail-grid">
                                  <div className="tx-detail-item">
                                    <span className="tx-detail-label">Fecha transaccion</span>
                                    <span className="tx-detail-value">{formatDate(row.timestampCreacion)}</span>
                                  </div>
                                  <div className="tx-detail-item">
                                    <span className="tx-detail-label">Fecha orden</span>
                                    <span className="tx-detail-value">{formatDate(row.fechaOrden)}</span>
                                  </div>
                                  <div className="tx-detail-item">
                                    <span className="tx-detail-label">Nro afiliado</span>
                                    <span className="tx-detail-value">{row.idAfiliado || "-"}</span>
                                  </div>
                                  <div className="tx-detail-item">
                                    <span className="tx-detail-label">Cantidad solicitada</span>
                                    <span className="tx-detail-value">{row.cantidadSolicitada || 0}</span>
                                  </div>
                                  <div className="tx-detail-item">
                                    <span className="tx-detail-label">Cantidad autorizada</span>
                                    <span className="tx-detail-value">{row.cantidadAutorizada || 0}</span>
                                  </div>
                                  <div className="tx-detail-item tx-detail-item--wide">
                                    <span className="tx-detail-label">Motivo</span>
                                    <span className="tx-detail-value">{getMotivo(row)}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </article>
                        );
                      })
                    )}
                  </div>
                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="tx-pagination">
                      <span className="tx-pagination-info">
                        Página {page + 1} de {pageCount}
                      </span>
                      <div className="tx-pagination-buttons">
                        {[
                          { label: "«", action: () => setPage(0),                 disabled: page === 0 },
                          { label: "‹", action: () => setPage((p) => p - 1),      disabled: page === 0 },
                          { label: "›", action: () => setPage((p) => p + 1),      disabled: page >= pageCount - 1 },
                          { label: "»", action: () => setPage(pageCount - 1),     disabled: page >= pageCount - 1 },
                        ].map((btn) => (
                          <button
                            key={btn.label}
                            className="tx-page-btn"
                            onClick={btn.action}
                            disabled={btn.disabled}
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
